import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { FiPlus, FiSearch, FiPlay } from 'react-icons/fi';
import TechPipelineModal from '../components/TechPipelineModal';
import TechPagination from '../components/TechPagination';
import TechUserFilter from '../components/TechUserFilter';
import { TYPE_COLORS, PRIORITY_COLORS, formatDateTimeShort } from '../utils/techTask';

const PAGE_SIZE = 10;

const TechPipeline = () => {
  const { user, isTechAdmin } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');
  const [assignee, setAssignee] = useState('all');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [opening, setOpening] = useState(null);
  const [message, setMessage] = useState('');

  const fetchTasks = async (p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        status: 'pipeline',
        page: String(p),
        limit: String(PAGE_SIZE),
      });
      if (search) params.set('search', search);
      if (assignee !== 'all') params.set('assigned_to', assignee);
      const res = await api.get(`/tech-tasks?${params.toString()}`);
      setTasks(res.data.tasks || []);
      setPagination(res.data.pagination || { total: 0, totalPages: 1, page: p });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks(page);
  }, [page, assignee]);

  const handleAssigneeChange = (id) => {
    setAssignee(id);
    setPage(1);
  };

  const handleSearch = () => {
    setPage(1);
    fetchTasks(1);
  };

  const openTask = async (id) => {
    setOpening(id);
    setMessage('');
    try {
      await api.post(`/tech-tasks/${id}/open`);
      setMessage('Task opened — moved to Tickets as pending.');
      fetchTasks(page);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Could not open task');
    } finally {
      setOpening(null);
    }
  };

  const canOpen = (task) =>
    !task.assigned_to || isTechAdmin || task.assigned_to === user?.id;

  return (
    <div className="space-y-4 max-w-6xl pb-8">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Pipeline tasks</h1>
          <p className="text-slate-400 text-sm mt-1">Future work — assign optionally, open when ready</p>
        </div>
        <button
          type="button"
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-teal-500 text-slate-950 font-semibold text-sm touch-manipulation"
        >
          <FiPlus size={18} />
          Add
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Search pipeline tasks…"
            className="w-full pl-10 pr-4 py-3 rounded-xl bg-slate-900 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
          />
        </div>
        <TechUserFilter value={assignee} onChange={handleAssigneeChange} />
      </div>

      {message && <p className="text-sm text-teal-300">{message}</p>}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-400" />
        </div>
      ) : tasks.length === 0 ? (
        <p className="text-center text-slate-500 py-12">No pipeline tasks yet.</p>
      ) : (
        <>
          <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px] text-sm">
                <thead>
                  <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-3 font-medium">Title</th>
                    <th className="px-4 py-3 font-medium">Client</th>
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium">Priority</th>
                    <th className="px-4 py-3 font-medium">Assignee</th>
                    <th className="px-4 py-3 font-medium">Planned</th>
                    <th className="px-4 py-3 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {tasks.map((t) => (
                    <tr key={t.id} className="hover:bg-slate-800/50">
                      <td className="px-4 py-3 font-medium text-white">{t.title}</td>
                      <td className="px-4 py-3 text-slate-400">{t.client_name || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`${TYPE_COLORS[t.task_type] || ''} text-[10px] px-2 py-0.5 rounded-full capitalize`}>
                          {t.task_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`${PRIORITY_COLORS[t.priority] || 'bg-slate-600'} text-white text-[10px] px-2 py-0.5 rounded-full capitalize`}>
                          {t.priority}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{t.assigned_to_name || '—'}</td>
                      <td className="px-4 py-3 text-slate-400 whitespace-nowrap text-xs">
                        {formatDateTimeShort(t.due_date)}
                      </td>
                      <td className="px-4 py-3">
                        {canOpen(t) ? (
                          <button
                            type="button"
                            disabled={opening === t.id}
                            onClick={() => openTask(t.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-500/20 text-teal-300 text-xs font-medium hover:bg-teal-500/30 disabled:opacity-50 touch-manipulation"
                          >
                            <FiPlay size={12} />
                            {opening === t.id ? 'Opening…' : 'Open task'}
                          </button>
                        ) : (
                          <span className="text-xs text-slate-500">Assigned to {t.assigned_to_name}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <TechPagination
            page={page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            onPageChange={setPage}
          />
        </>
      )}

      {showModal && (
        <TechPipelineModal
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            setPage(1);
            fetchTasks(1);
          }}
        />
      )}
    </div>
  );
};

export default TechPipeline;
